import React, { useState } from 'react';
import API from '../api';
import { CreditCard, Smartphone, Landmark, Wallet, X, Shield, CheckCircle, Info, ArrowRight } from 'lucide-react';

function PaymentModal({ session, sessionType, onClose, onPaymentComplete }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load Razorpay Script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    try {
      // 1. Load SDK
      const sdkLoaded = await loadRazorpayScript();
      if (!sdkLoaded) {
        throw new Error('Razorpay SDK failed to load. Check internet connection.');
      }

      // 2. Create Order on Backend
      const orderResponse = await API.post('/razorpay/create-order', {
        session_id: session.id,
        session_type: sessionType, // "PUBLIC", "REQUEST", etc.
        amount: session.price || session.budget || 0
      });

      const { order_id, amount, currency, key_id, payment_db_id } = orderResponse.data;

      // 3. Configure Options
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'Professor of Practice',
        description: session.title || 'Session Payment',
        order_id: order_id,
        handler: async function (response) {
          try {
            // 4. Verify Payment on Backend
            const verifyResponse = await API.post('/razorpay/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyResponse.data.success) {
              if (onPaymentComplete) {
                onPaymentComplete({
                  payment_id: payment_db_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  status: 'SUCCESS'
                });
              }
              onClose(); // Close modal on success
            }
          } catch (err) {
            console.error("Verification Error", err);
            setError('Payment successful but verification failed. Please contact support.');
          }
        },
        prefill: {
          name: localStorage.getItem('userName') || '',
          email: localStorage.getItem('userEmail') || '',
          contact: localStorage.getItem('userPhone') || ''
        },
        theme: {
          color: '#1e3a8a'
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
          }
        }
      };

      // 4. Open Checkout
      const rzp = new window.Razorpay(options);
      rzp.open();
      // Keep loading true until modal dismissed or payment success

    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || err.message || 'Payment initiation failed');
      setLoading(false);
    }
  };

  return (
    <div className="pay-modal-overlay">
      <div className="pay-modal-container animate-slide-up">
        {/* Header */}
        <div className="pay-modal-header shadow-premium">
          <div className="pay-header-content">
            <h2 className="title-premium">Complete Payment</h2>
            <p className="subtitle-premium">
              Amount: ₹{(session.price || session.budget || 0).toLocaleString('en-IN')}
            </p>
          </div>
          <button className="pay-close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="pay-modal-body">
          {/* Escrow Badge */}
          <div className="escrow-safe-badge">
            <Shield size={18} className="shield-icon" />
            <div className="escrow-text">
              <strong>Secure Escrow</strong>
              <span>Funds held by compliance until session completion.</span>
            </div>
          </div>

          {/* Session Summary */}
          <div className="pay-session-summary">
            <div className="summary-item">
              <span className="label">Session</span>
              <span className="value">{session.title || 'Industry Consultation'}</span>
            </div>
            <div className="summary-item total">
              <span className="label">Total Payable</span>
              <span className="value">₹{(session.price || session.budget || 0).toLocaleString('en-IN')}</span>
            </div>
          </div>

          {error && <div className="pay-error-alert animate-shake" style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}

          <button
            className={`pay-submit-btn ${loading ? 'loading' : ''}`}
            onClick={handlePayment}
            disabled={loading}
          >
            {loading ? (
              <div className="spinner-small"></div>
            ) : (
              <>Pay Now <ArrowRight size={18} /></>
            )}
          </button>

          <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#666', marginTop: '10px' }}>
            Secured by Razorpay
          </p>
        </div>

        <style>{`
          .pay-modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
            backdrop-filter: blur(5px);
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 16px;
          }

          .pay-modal-container {
            background: white;
            width: 100%;
            max-width: 450px;
            max-height: 90vh;
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
            animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1);
            display: flex;
            flex-direction: column;
          }

          .pay-modal-header {
            background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
            padding: 20px 24px;
            color: white;
            position: relative;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-shrink: 0;
          }

          .title-premium { margin: 0; font-size: 1.25rem; font-weight: 700; letter-spacing: -0.01em; }
          .subtitle-premium { margin: 2px 0 0 0; opacity: 0.9; font-size: 0.85rem; }

          .pay-close-btn { 
            background: rgba(255, 255, 255, 0.2); 
            border: none; color: white; 
            padding: 8px; border-radius: 50%; 
            cursor: pointer; transition: all 0.2s;
            display: flex; align-items: center; justify-content: center;
          }
          .pay-close-btn:hover { background: rgba(255, 255, 255, 0.3); transform: rotate(90deg); }

          .pay-modal-body { 
            padding: 24px; 
            overflow-y: auto;
            flex: 1;
          }

          .escrow-safe-badge {
            display: flex; gap: 12px; background: #f0fdf4; 
            border: 1px solid #dcfce7; padding: 12px;
            border-radius: 12px; margin-bottom: 20px;
            align-items: flex-start;
          }
          .shield-icon { color: #10b981; flex-shrink: 0; margin-top: 2px; }
          .escrow-text { display: flex; flex-direction: column; }
          .escrow-text strong { font-size: 0.85rem; color: #064e3b; margin-bottom: 2px; }
          .escrow-text span { font-size: 0.75rem; color: #059669; line-height: 1.3; }

          .pay-session-summary { margin-bottom: 24px; }
          .summary-item { display: flex; justify-content: space-between; margin-bottom: 10px; }
          .summary-item .label { color: #6b7280; font-size: 0.85rem; }
          .summary-item .value { color: #111827; font-weight: 600; font-size: 0.85rem; }
          .summary-item.total { border-top: 1px dashed #e5e7eb; padding-top: 10px; margin-top: 10px; }
          .summary-item.total .label { font-weight: 700; color: #111827; }
          .summary-item.total .value { color: #1e3a8a; font-size: 1.15rem; font-weight: 800; }

          .pay-submit-btn {
            width: 100%; background: #1e3a8a; color: white; border: none; padding: 14px;
            border-radius: 12px; font-weight: 700; cursor: pointer; display: flex;
            align-items: center; justify-content: center; gap: 8px; transition: all 0.2s;
            font-size: 0.95rem; margin-top: auto;
          }
          .pay-submit-btn:hover:not(:disabled) { background: #1e40af; transform: translateY(-1px); }
          .pay-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }

          .animate-slide-up { animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1); }
          
          @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes spin { to { transform: rotate(360deg); } }
          .spinner-small { width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3); border-top-color: white; border-radius: 50%; animation: spin 0.8s linear infinite; }
        `}</style>
      </div>
    </div>
  );
}

export default PaymentModal;
